import {BreakpointSize, Grid, Pillar, Image, Statistic, Text, useWindowSize} from '@primer/react-brand'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import SectionHero, {type SectionHeroProps} from './components/SectionHero/SectionHero'
import SectionIntro from './components/SectionIntro/SectionIntro'
import useFootnotes from './hooks/useFootnotes'
import Footnote from './components/Footnote/Footnote'
import FootnoteToggle from './components/Footnote/FootnoteToggle/FootnoteToggle'
import React, {useEffect, useMemo, useState} from 'react'

const COPY = {
  analyticsId: 'security',
  intro: {
    title: 'Built-in application security <br class="lp-breakText" /> where found means fixed',
    description: 'Use AI to find and fix vulnerabilities—freeing your teams to ship more secure software faster.',
  },
  hero: {
    title: 'Apply fixes in seconds.',
    description: ' Spend less time fixing vulnerabilities and more time building features with Copilot Autofix.',
    link: {
      url: '/enterprise/advanced-security',
      label: 'Explore GitHub Advanced Security',
    },
    aria: {
      // TODO: Update once known
      playButton: {
        play: 'Play',
        pause: 'Pause',
      },
    },
  },
  topics: [
    {
      title: 'Solve security debt.',
      description:
        ' Leverage AI-assisted security campaigns to reduce application vulnerabilities and zero-day attacks.',
      link: {
        url: '/enterprise/advanced-security',
        label: 'Discover security campaigns',
      },
      visual: {
        url: '/images/modules/site/home24/security/pillar-1.webp',
        alt: 'A security campaign screen displays the campaign’s progress bar with 97% completed of 701 alerts. A total of 23 alerts are left with 13 in progress, and the campaign started 20 days ago. The status below shows that there are 7 days left in the campaign with a due date of November 15, 2024.',
      },
    },
    {
      title: 'Dependencies you can depend on.',
      description: ' Update vulnerable dependencies with supported fixes for breaking changes.',
      link: {
        url: '/features/security/software-supply-chain',
        label: 'Learn about Dependabot',
      },
      visual: {
        url: '/images/modules/site/home24/security/pillar-2.webp',
        alt: 'List of dependencies defined in a requirements .txt file.',
      },
    },
    {
      title: 'Your secrets, your business: protected. ',
      description: ' Detect, prevent, and remediate leaked secrets across your organization.',
      link: {
        url: '/features/security/code',
        label: 'Read about secret scanning',
      },
      visual: {
        url: '/images/modules/site/home24/security/pillar-3.webp',
        alt: 'GitHub push protection confirms and displays an active secret, and blocks the push.',
      },
    },
  ],
  statistics: [
    {
      heading: '7x faster',
      description: ' vulnerability fixes with GitHub',
      footnote: {
        number: 2,
        text: 'This 7X times factor is based on data from the industry’s longest running analysis of fix rates Veracode State of Software Security 2023, which cites the average time to fix 50% of flaws as 198 days vs. GitHub’s fix rates of 72% of flaws with in 28 days which is at a minimum of 7X faster when compared.',
      },
    },
    {
      heading: '90% coverage',
      description: ' of alert types in all supported languages with Copilot Autofix',
      footnote: {
        number: 3,
        // eslint-disable-next-line github/unescaped-html-literal
        text: '<a href="https://docs.github.com/en/code-security/code-scanning/managing-your-code-scanning-configuration/codeql-query-suites">https://docs.github.com/en/code-security/code-scanning/managing-your-code-scanning-configuration/codeql-query-suites</a>',
      },
    },
  ],
}

const HERO_VISUALS: SectionHeroProps['visuals'] = [
  {
    type: 'image',
    url: {
      desktop: '/images/modules/site/home24/security/hero.webp',
    },
    alt: 'Copilot Autofix identifies vulnerable code and provides an explanation, together with a secure code suggestion to remediate the vulnerability.',
  },
]

export default function Security() {
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const {footnoteStates, initFootnotes, onFootnoteClick, onFootnoteEnter, onFootnoteKeyDown} = useFootnotes()
  const windowSize = useWindowSize()

  const hasEmptyCell = useMemo(
    () =>
      (windowSize.currentBreakpointSize === BreakpointSize.MEDIUM && COPY.topics.length % 2 !== 0) ||
      ([BreakpointSize.LARGE, BreakpointSize.XLARGE, BreakpointSize.XXLARGE].includes(
        windowSize.currentBreakpointSize!,
      ) &&
        COPY.topics.length % 3 !== 0),
    [windowSize],
  )

  const statisticsDOM = useMemo(() => {
    const WrapperElement = hasEmptyCell ? React.Fragment : Grid

    return (
      <WrapperElement {...(hasEmptyCell ? {} : {className: 'Security-grid'})}>
        {COPY.statistics.map((statistic, index) => (
          <Grid.Column
            className="Security-gridColumn Security-gridColumn--half Security-gridColumn--centered"
            span={{xsmall: 12, medium: 6}}
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={`statistic_${index}`}
          >
            <Statistic>
              <Statistic.Heading className="Security-statisticHeading" size="4">
                {statistic.heading}
              </Statistic.Heading>

              <Statistic.Description className="Security-statisticDescription">
                {statistic.description}
                {statistic.footnote && (
                  <FootnoteToggle
                    isExpanded={!!footnoteStates[statistic.footnote.number]}
                    number={statistic.footnote.number}
                    onClick={onFootnoteClick}
                    onEnter={onFootnoteEnter}
                    onKeyDown={onFootnoteKeyDown}
                  />
                )}
              </Statistic.Description>
            </Statistic>

            {statistic.footnote && (
              <Footnote {...statistic.footnote} isVisible={!!footnoteStates[statistic.footnote.number]} />
            )}
          </Grid.Column>
        ))}
      </WrapperElement>
    )
  }, [footnoteStates, hasEmptyCell, onFootnoteClick, onFootnoteEnter, onFootnoteKeyDown])

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isMounted) return

    const footnoteNumbers = COPY.statistics
      .filter(statistic => statistic.footnote && Number.isFinite(statistic.footnote?.number))
      .map(statistic => statistic.footnote.number)

    initFootnotes(footnoteNumbers)
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted])

  return (
    <section id="security" className="lp-Section">
      <div className="lp-SectionBlock">
        <div className="lp-SectionBlock-content">
          <SectionIntro mascot="shield" title={COPY.intro.title} description={COPY.intro.description} />

          <SectionHero
            visuals={HERO_VISUALS}
            playButtonAriaLabel={COPY.hero.aria.playButton}
            text={{
              title: COPY.hero.title,
              description: COPY.hero.description,
              link: {
                url: COPY.hero.link.url,
                label: COPY.hero.link.label,
              },
            }}
            analyticsId={COPY.analyticsId}
          />
        </div>
      </div>

      <div className="lp-SectionBlock">
        <div className="lp-SectionBlock-content lp-SectionBlock-content--lines">
          <Grid className="Security-grid Security-grid--hasPillars">
            {COPY.topics.map((topic, index) => (
              <Grid.Column
                className="Security-gridColumn"
                span={{xsmall: 12, medium: 6, large: 4}}
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`topic_${index}`}
              >
                <Pillar>
                  <Pillar.Description className="Security-pillarDescription">
                    <Text size="300">
                      <span>{topic.title}</span>
                      {topic.description}
                    </Text>
                  </Pillar.Description>

                  <Pillar.Link
                    href={topic.link.url}
                    {...getAnalyticsEvent({
                      action: topic.link.label,
                      tag: 'link',
                      context: topic.title,
                      location: COPY.analyticsId,
                    })}
                  >
                    <Text weight="normal">{topic.link.label}</Text>
                  </Pillar.Link>
                </Pillar>

                <Image className="Security-gridImage" src={topic.visual.url} alt={topic.visual.alt} loading="lazy" />
              </Grid.Column>
            ))}

            {hasEmptyCell && (
              <Grid.Column
                className="Security-gridColumn Security-gridColumn--statistics"
                span={{xsmall: 12, medium: 6, large: 4}}
              >
                {statisticsDOM}
              </Grid.Column>
            )}
          </Grid>

          {!hasEmptyCell && statisticsDOM}
        </div>
      </div>
    </section>
  )
}

try{ Security.displayName ||= 'Security' } catch {}